import React, { useState } from 'react'
import { Card, Button, Typography, Input, Row, Col, Upload, Modal, notification, Image } from 'antd'
import PropTypes from 'prop-types'
import { fetchPresignedUrl, uploadFileToS3WithPresignedUrl } from '~actions'
import { sanitizeFileName } from '~utils'
import styles from './PhotoSettings.module.css'

const PhotoSettings = ({ alt, handleIconChange, isOpen, logoUrl, setIsOpen }) => {
  const { TextArea } = Input
  const { Title } = Typography
  const { Dragger } = Upload
  const [fileList, setFileList] = useState([{
    url: logoUrl,
    caption: '',
    title: '',
    copyUrl: '',
    description: '',
    alt
  }])

  const fileToS3 = async (file) => {
    const isImage = file.type.startsWith('image/')
    const isSVG = file.type === 'image/svg+xml'
    if (!isImage && !isSVG) {
      notification.warning({ message: 'You can only upload image files (JPG, PNG, JPEG, SVG)' })
    }
    try {
      const formattedFile = sanitizeFileName(file?.name)
      const response = await fetchPresignedUrl({
        fileName: `event-engine/${formattedFile}`,
        fileType: file.type
      })
      const { presignedPutUrl, url } = response
      await uploadFileToS3WithPresignedUrl(presignedPutUrl, file)
      setFileList([{ file }])
      return url
    } catch (error) {
      console.error(error)
    }
  }
  const handleChange = async ({ file }) => {
    const url = await fileToS3(file)
    setFileList([{
      ...fileList[0],
      url: url
    }])
  }

  const uploadFileToS3 = async ({ file, onSuccess, onError }) => {
    const url = await fileToS3(file)
    setFileList([{
      ...fileList[0],
      url: url
    }])
  }
  const handleOnModalCancel = () => {
    setIsOpen(false)
  }
  const handleUpdateSettings = () => {
    setIsOpen(false)
    handleIconChange(fileList[0])
  }
  const handleAltTxt = ({ target }) => {
    setFileList([{
      ...fileList[0],
      alt: target.value
    }])
  }

  const handleCaptionChange = (val) => {
    setFileList([{ ...fileList[0], caption: val }])
  }
  const handleCopyUrl = (val) => {
    setFileList([{ ...fileList[0], copyUrl: val }])
  }
  const handleDescriptionChange = (val) => {
    setFileList([{ ...fileList[0], description: val }])
  }
  const handleTitleChange = (val) => {
    setFileList([{ ...fileList[0], title: val }])
  }
  return (
    <Modal
      centered
      className={styles['settings-container']}
      okText="Update"
      onCancel={handleOnModalCancel}
      onOk={handleUpdateSettings}
      title="Edit Image"
      visible={isOpen}
      width={{ sm: '80%' }}
    >
      <Card className={styles['card-container']}>
        <Row align={'middle'} className={styles['title-wrapper']}>
          <Col><Title level={3}>Image*</Title></Col>
          <Col><Title level={4}>(Upload PNG or JPG; preferred dimensions 0px x 0px; max file size 1MB)</Title></Col>
        </Row>
        <Row gutter={[20, 20]}>
          { fileList[0]?.url
            ? (<Col span={6}>
              <Image
                src={fileList[0]?.url}
                style={{ maxHeight: 'unset', height: 'auto' }}
              />
            </Col>)
            : (<Col span={6}>
              <Dragger
                accept='image/*,image/svg+xml'
                beforeUpload={() => false}
                fileList={fileList}
                maxCount={1}
                onChange={handleChange}
                onRemove={() => setFileList({})}
              />
            </Col>)
          }
          <Col span={18}>
            <Upload accept='image/*' customRequest={uploadFileToS3} maxCount={1} showUploadList={false}>
              <Button style={{ background: '#0078D4', borderColor: '#0078D4', fontSize: 14 }} type="primary">Replace</Button>
            </Upload>
          </Col>
        </Row>
      </Card>
      <Card className={styles['card-container']}>
        <Row align={'middle'} className={styles['title-wrapper']}>
          <Col><Title level={3}>Alt-Text</Title></Col>
          <Col><Title level={4}>&nbsp;(70 characters max. Describe the purpose of the image; leave empty if the image is purely decorative.)</Title></Col>
        </Row>
        <Input maxLength="70" onChange={handleAltTxt} type="text" value={fileList[0]?.alt || ''} />
      </Card>
      <Card className={styles['card-container']}>
        <Row align={'middle'} className={styles['title-wrapper']}>
          <Col><Title level={3}>Title </Title></Col>
          <Col><Title level={4}>&nbsp;(70 characters max; name of event)</Title></Col>
        </Row>
        <Input maxLength="70" onChange={({ target }) => handleTitleChange(target.value)} type="text" value={fileList[0]?.title || ''} />
      </Card>
      <Card className={styles['card-container']}>
        <Row align={'middle'} className={styles['title-wrapper']}>
          <Col><Title level={3}>Caption </Title></Col>
          <Col><Title level={4}>&nbsp;(3000 characters max; input event details , such as performer/team summary, opening act, event activations, etc.)</Title></Col>
        </Row>
        <TextArea maxLength={3000} onChange={({ target }) => handleCaptionChange(target.value)} rows={4} value={fileList[0]?.caption || ''}/>
      </Card>
      <Card className={styles['card-container']}>
        <Row align={'middle'} className={styles['title-wrapper']}>
          <Col><Title level={3}>Description</Title></Col>
          <Col><Title level={4}>&nbsp;(3000 characters max; input event details , such as performer/team summary, opening act, event activations, etc.)</Title></Col>
        </Row>
        <TextArea maxLength={3000} onChange={({ target }) => handleDescriptionChange(target.value)} rows={4} value={fileList[0]?.description || ''}/>
      </Card>
      <Card className={styles['card-container']}>
        <Row align={'middle'} className={styles['title-wrapper']}>
          <Col><Title level={3}>Copy Link</Title></Col>
          <Col><Title level={4}>&nbsp;70 characters max; name of event)</Title></Col>
        </Row>
        <Input maxLength="70" onChange={({ target }) => handleCopyUrl(target.value)} type="url" value={fileList[0]?.copyUrl || ''} />
      </Card>
    </Modal>
  )
}

PhotoSettings.propTypes = {
  alt: PropTypes.string,
  handleIconChange: PropTypes.func,
  isOpen: PropTypes.bool,
  logoUrl: PropTypes.string,
  setIsOpen: PropTypes.func
}

export default PhotoSettings
